<script setup>

import {storeToRefs} from "pinia";
import {useOrdersStore} from "@/stores/order.store";
import {useRoute} from "vue-router/dist/vue-router";
import {onMounted} from "vue";
import useDebounce from "@/helpers/debouncer";

const route = useRoute();
const orderStore = useOrdersStore();
const {order, payment} = storeToRefs(orderStore)

onMounted(async () => {
    await orderStore.fetchOne(route.params.id);
})

const createPayment = async () => {
    useDebounce(await orderStore.pay());
}

</script>

<template>
    <van-space direction="vertical" fill>
        <van-cell-group inset>
            <van-card
                v-for="product in order.products"
                currency="$"
                :key="product.id"
                :num="1"
                :price="order.total_amount"
                :title="product.name"
                :thumb="product.thumbnail_file_absolute_path"
            />
            <van-cell title="Order #" :value="order.id"/>
            <van-cell title="Status" :value="order.status"/>
            <van-cell title="Date" :value="order.created_at_formatted"/>
        </van-cell-group>

        <van-form v-if="order.status !== 'completed'" @submit="createPayment">
            <van-row justify="center">
                <b>Payment Information</b>
            </van-row>
            <van-cell-group inset>
                <van-field
                    placeholder="Name"
                    v-model="payment.user_payment_json.credit_card_name"
                    required
                    :rules="[{ required: true, message: 'Please input your name' }]"
                />
                <div style="display: flex">
                    <van-field
                        placeholder="Credit Card"
                        v-model="payment.user_payment_json.credit_card_number"
                        type="tel"
                        required
                        :rules="[
                            { required: true, message: 'Credit card is required' },
                            { pattern: /^(?:4[0-9]{12}(?:[0-9]{3})?|[25][1-7][0-9]{14}|6(?:011|5[0-9]{2})[0-9]{12}|3[47][0-9]{13}|3(?:0[0-5]|[68][0-9])[0-9]{11}|(?:2131|1800|35\d{3})\d{11})$/, message: 'Invalid credit card number' }
                           ]"
                    />
                    <van-field
                        placeholder="CVV"
                        type="number"
                        v-model="payment.user_payment_json.credit_card_cvv"
                        required
                        :rules="[
                        { required: true, message: 'CVV is required' },
                        { pattern: /^\d{3,4}$/, message: 'Invalid CVV' }
                     ]"
                    />
                </div>
                <div style="display: flex">
                    <van-field
                        placeholder="Expire Month (MM)"
                        type="number"
                        v-model="payment.user_payment_json.expiry_month"
                        required
                        :rules="[
                        { required: true, message: 'Month is required' },
                        { pattern: /^(0?[1-9]|1[012])$/, message:'Invalid month'}
                      ]"
                    />
                    <van-field
                        placeholder="Expire Year (YYYY)"
                        type="number"
                        v-model="payment.user_payment_json.expiry_year"
                        required
                        :rules="[
                        { required: true, message: 'Year is required' },
                        { pattern: /^(20[2-9]\d)$/, message: 'Invalid year' }
                      ]"
                    />
                </div>
            </van-cell-group>

            <van-row>&nbsp;</van-row>
            <van-row justify="center">
                <b>Billing Information</b>
            </van-row>
            <van-cell-group inset>
                <div style="display: flex">
                    <van-field
                        placeholder="First Name"
                        v-model="payment.user_billing_json.first_name"
                        required
                        :rules="[{ required: true, message: 'First name is required' }, { pattern: /^[a-zA-Z\s-]+$/, message: 'Invalid characters' }]"
                    >
                    </van-field>
                    <van-field
                        placeholder="Last Name"
                        v-model="payment.user_billing_json.last_name"
                        required
                        :rules="[{ required: true, message: 'Last name is required' }, { pattern: /^[a-zA-Z\s-]+$/, message: 'Invalid characters' }]"
                    />
                </div>
                <van-field
                    placeholder="Address"
                    v-model="payment.user_billing_json.address1"
                    required
                    :rules="[{ required: true, message: 'Address is required' }]"
                />
                <div style="display: flex">
                    <van-field
                        placeholder="City"
                        v-model="payment.user_billing_json.city"
                        required
                        :rules="[{ required: true, message: 'City is required' }]"
                    />
                    <van-field
                        placeholder="State"
                        v-model="payment.user_billing_json.state"
                        required
                        :rules="[
                            { required: true, message: 'State is required' },
                            { pattern: /^(AL|AK|AZ|AR|CA|CO|CT|DE|FL|GA|HI|ID|IL|IN|IA|KS|KY|LA|ME|MD|MA|MI|MN|MS|MO|MT|NE|NV|NH|NJ|NM|NY|NC|ND|OH|OK|OR|PA|RI|SC|SD|TN|TX|UT|VT|VA|WA|WV|WI|WY)$/, message: 'Invalid State' }
                         ]"
                    />

                </div>
                <van-field
                    placeholder="Zip"
                    type="tel"
                    v-model="payment.user_billing_json.zip"
                    required
                    :rules="[{ required: true, message: 'Zip code is required' }, { pattern: /^\d{5}$/, message: 'Invalid zip code' }]"
                />
            </van-cell-group>

            <van-submit-bar label="Total">
                <template #button>
                    <van-button size="small" round block type="danger" native-type="submit">
                        Pay Now <b>${{ order.total_amount }}</b>
                    </van-button>
                </template>
                <van-action-bar-icon icon="home-o" text="Home" to="/"/>
            </van-submit-bar>
        </van-form>
        <div v-else>
            <van-row>&nbsp;</van-row>
            <van-row justify="center">
                <b>Treatment Sessions</b>
            </van-row>
            <van-row>&nbsp;</van-row>
            <van-cell-group inset>
                <div v-for="orderProduct in order.order_products" :key="orderProduct.id" >
                    <div v-for="userTreatmentSession in orderProduct.user_treatment_sessions" :key="userTreatmentSession.id" >
                        <van-cell
                            :title="userTreatmentSession.name"
                            :value="userTreatmentSession.status"
                            :to="'/session/' + userTreatmentSession.id"
                            is-link
                        />
                    </div>
                </div>
            </van-cell-group>

            <div v-if="payment.user_payment_json.credit_card_name">
                <van-row>&nbsp;</van-row>
                <van-row justify="center">
                    <b>Payment Information</b>
                </van-row>
                <van-row>&nbsp;</van-row>
                <van-cell-group inset>
                    <van-cell title="Name" :value="payment.user_payment_json.credit_card_name"/>
                    <van-cell title="Credit Card #" :value="payment.user_payment_json.credit_card_number"/>
                </van-cell-group>

                <van-row>&nbsp;</van-row>
                <van-row justify="center">
                    <b>Billing Information</b>
                </van-row>
                <van-row>&nbsp;</van-row>
                <van-cell-group inset>
                    <van-cell title="Name" :value="payment.user_billing_json.first_name + ' ' + payment.user_billing_json.last_name"/>
                    <div style="display: flex">
                        <van-cell title="Address" :value="payment.user_billing_json.address1 "/>
                        <van-cell title="Zip" :value="payment.user_billing_json.zip "/>
                    </div>
                    <div style="display: flex">
                        <van-cell title="City" :value="payment.user_billing_json.city "/>
                        <van-cell title="State" :value="payment.user_billing_json.state "/>
                    </div>
                </van-cell-group>
            </div>
        </div>


    </van-space>
</template>
